import { EmptyState, ExclamationmarkIcon, Modal } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { parseScoreDetails } from '../../../utils/timetable/solverDiagnostics';
import { OmniChip } from '../../Solver/OmniChip';
import { useTimetableStore } from '../TimetableProvider';
import { useConstraintLabel } from '../useConstraintLabel';
import styles from './ScoreDebugModal.module.scss';

type Props = { isOpen: boolean; onClose: () => void };

/**
 * Admin-only debug surface that shows the score breakdown the solver job already
 * carries (`scoreDetails`, populated by polling) — it does NOT run a fresh analysis.
 * Reading straight from the live job means the monitor block (poll count, "unchanged
 * for X s") is exactly what helps diagnose a solver that has stalled / hung.
 */
export const ScoreDebugModal: FC<Props> = observer(({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const constraintLabel = useConstraintLabel();
  const pinboardStore = useTimetableStore();

  // „Score unverändert seit …“: ab einer Minute in Minuten, ab einer Stunde in Stunden und Minuten.
  const durationText = (seconds: number): string => {
    if (seconds < 60) return t('pinboard.generate.runBanner.scoreDebugModal.seconds', { seconds });
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t('pinboard.generate.runBanner.scoreDebugModal.minutes', { minutes });
    return t('pinboard.generate.runBanner.scoreDebugModal.hours', {
      hours: Math.floor(minutes / 60),
      minutes: minutes % 60,
    });
  };
  const job = pinboardStore.solverStore.solverJob;
  const raw = job?.scoreDetails ?? null;
  const parsed = parseScoreDetails(raw);
  const details = parsed && parsed !== 'error' ? parsed : null;
  const rows = details?.breakdownFinal?.top ?? details?.breakdownLight?.top ?? [];

  const jsonFormatted = (() => {
    if (!raw) return '';
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  })();

  return (
    <Modal
      className={styles.modal}
      isOpen={isOpen}
      onRequestClose={onClose}
      title={t('pinboard.generate.runBanner.scoreDebugModal.title')}
      actions={
        // Die Kopfzeile des Modals zieht ihr erstes Element auf volle Breite – der Wrapper hält den Chip klein.
        <span className={styles['header-chip']}>
          <OmniChip />
        </span>
      }
    >
      {!raw ? (
        <EmptyState
          size='small'
          subtitle={t('pinboard.generate.runBanner.scoreDebugModal.empty')}
          icon={<ExclamationmarkIcon />}
          iconColor='var(--color-grey-dark)'
        />
      ) : (
        <div className={styles.body}>
          {details ? (
            <dl className={styles.meta}>
              <div>
                <dt>{t('pinboard.generate.runBanner.scoreDebugModal.solverStatus')}</dt>
                <dd>{details.status?.solverStatus ?? details.status?.name ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('pinboard.generate.runBanner.scoreDebugModal.score')}</dt>
                <dd>
                  {details.status?.score
                    ? `${details.status.score.hard} / ${details.status.score.medium} / ${details.status.score.soft}`
                    : '—'}
                </dd>
              </div>
              {details.monitor ? (
                <>
                  <div>
                    <dt>{t('pinboard.generate.runBanner.scoreDebugModal.polls')}</dt>
                    <dd>{details.monitor.pollCount ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>{t('pinboard.generate.runBanner.scoreDebugModal.unchanged')}</dt>
                    <dd>
                      {details.monitor.unchangedForSeconds != null
                        ? durationText(details.monitor.unchangedForSeconds)
                        : '—'}
                    </dd>
                  </div>
                </>
              ) : null}
            </dl>
          ) : (
            <div className={styles.parseError}>{t('pinboard.generate.runBanner.scoreDebugModal.parseError')}</div>
          )}

          {rows.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('pinboard.timefoldAnalyze.colConstraint')}</th>
                  <th className={styles.num}>{t('pinboard.timefoldAnalyze.colHard')}</th>
                  <th className={styles.num}>{t('pinboard.timefoldAnalyze.colMedium')}</th>
                  <th className={styles.num}>{t('pinboard.timefoldAnalyze.colSoft')}</th>
                  <th className={styles.num}>{t('pinboard.timefoldAnalyze.colMatches')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={`${row.name}-${String(i)}`}>
                    <td>
                      <div>{constraintLabel(row.name)}</div>
                      <div className={styles.cid}>{row.name}</div>
                    </td>
                    <td className={styles.num}>{row.hardScore}</td>
                    <td className={styles.num}>{row.mediumScore}</td>
                    <td className={styles.num}>{row.softScore}</td>
                    <td className={styles.num}>{row.matchCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}

          <details className={styles.rawDetails}>
            <summary className={styles.rawSummary}>{t('pinboard.generate.runBanner.scoreDebugModal.rawJson')}</summary>
            <pre className={styles.jsonPre}>{jsonFormatted}</pre>
          </details>
        </div>
      )}
    </Modal>
  );
});

ScoreDebugModal.displayName = 'ScoreDebugModal';
